import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { formatUnits, Provider, Signer } from 'ethers';
import { Errors } from '@blink/components/src/constants/errors';
import Logger from '@blink/components/src/utils/logger';

import { getWalletInfoSaga } from './common';

import {
    agreementContractErrorAction,
    agreementGeneralErrorAction,
    agreementLeverageErrorAction,
    agreementRoleErrorAction,
    clearErrorsAction,
    fetchAgreementsFromContractErrorAction,
    tokenBalanceErrorAction,
    tokenFetchErrorAction,
    tokenPriceErrorAction,
    walletBalanceErrorAction,
} from '@/shared/store/errors/actions';
import {
    FETCH_AGREEMENTS_FROM_CONTRACT,
    GET_AGREEMENTS,
    GET_USER_WALLET_BALANCE,
} from '@/shared/store/agreements/constants';
import {
    setAgreementsLoadingFalseAction,
    setBorrowerAgreementsAction,
    setLenderAgreementsAction,
    setUserBorrows,
} from '@/shared/store/agreements/actions';
import {
    setAgreementTokensAction,
    setTokensAction,
    setTokensLoadingFalseAction,
} from '@/shared/store/tokens/actions';
import { mapAgreementData } from '@/shared/utils/helpers';
import AgreementABI from '@/shared/abi/AgreementABI.json';
import IERCAbi from '@/shared/abi/IERCAbi.json';
import {
    Agreement,
    AgreementDetails,
    getAgreementDetails,
    getAgreements,
    getPrice,
    getTokens,
    Metadata,
    Price,
    ROLE,
    State,
    Token,
} from '@/shared/api/borrow';
import { getSafeContract } from '@/shared/utils/getSafeContract';
import {
    AGREEMENT_CONTRACT_ERROR,
    AGREEMENT_DETAILS_ERROR,
    AGREEMENT_GENERAL_ERROR,
    AGREEMENT_ROLE_ERROR,
    AGREEMENTS_ERROR,
    TOKEN_BALANCE_ERROR,
    TOKENS_ERROR,
    WALLET_BALANCE_ERROR,
} from '@/shared/store/errors/constants';
import { Store } from '@/shared/store';

const excludedAddress = [
    '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE',
    '0x0000000000000000000000000000000000000348',
    '0xbBbBBBBbbBBBbbbBbbBbbbbBBbBbbbbBbBbbBBbB',
];

const USD_ADDRESS = '0x0000000000000000000000000000000000000348';

/**
 *  Clear all agreement errors
 */
export function* clearAgreementErrors() {
    yield all([
        put(clearErrorsAction({ errorType: AGREEMENTS_ERROR })),
        put(clearErrorsAction({ errorType: TOKENS_ERROR })),
        put(clearErrorsAction({ errorType: AGREEMENT_CONTRACT_ERROR })),
        put(clearErrorsAction({ errorType: AGREEMENT_DETAILS_ERROR })),
        put(clearErrorsAction({ errorType: AGREEMENT_ROLE_ERROR })),
        put(clearErrorsAction({ errorType: AGREEMENT_GENERAL_ERROR })),
    ]);
}

/**
 *  Loading tokens and agreements
 */
export function* fetchTokensAndAgreements(withoutCache?: boolean) {
    try {
        const [tokens, agreements]: [Token[], Agreement[]] = yield all([
            call(getTokens),
            call(getAgreements, withoutCache),
        ]);

        yield put(setAgreementsLoadingFalseAction());

        if (!tokens) {
            yield put(tokenFetchErrorAction({ message: Errors.TOKENS }));
            yield put(setTokensLoadingFalseAction());
            return null;
        } else {
            yield put(setTokensAction(tokens));
        }

        return { tokens, agreements };
    } catch (e: any) {
        yield put(
            agreementGeneralErrorAction({
                message:
                    e?.status === 403 || e?.status === 431
                        ? Errors.PERMISSION_DENIED + ' to get agreements'
                        : Errors.NO_AVAILABLE_AGREEMENTS + ' ' + e.message,
            }),
        );
        return null;
    }
}

/**
 *  Filter tokens and agreements by role
 */
export function* filterTokensAndAgreements(
    tokens: Token[],
    agreements: Agreement[],
    agreementRole: ROLE,
    isNoWallet?: boolean,
) {
    const filteredTokens = tokens.filter(
        (token) => !excludedAddress.includes((token.address || '').trim()),
    );

    let filteredAgreements = agreements;
    if (agreementRole) {
        filteredAgreements = agreements.filter((agreement) => agreement.role === agreementRole);
    }

    if (agreementRole && !filteredAgreements.length) {
        yield put(
            agreementRoleErrorAction({ message: Errors.NO_AGREEMENTS_WITH_ROLE + agreementRole }),
        );
        return null;
    }

    const tokensByAgreements = filteredAgreements
        .map((agreement) => {
            const token = filteredTokens.find(
                (filteredToken) => filteredToken.id === agreement.leverageTokenId,
            );

            if (!token) {
                return null;
            }

            token!.agreementAddress = agreement.address;
            agreement.noWalletInfo = isNoWallet || false;
            return token as Token;
        })
        .filter(Boolean) as Token[];

    if (!tokensByAgreements.length) {
        const action =
            agreementRole === ROLE.BORROWER
                ? setBorrowerAgreementsAction
                : setLenderAgreementsAction;
        yield put(action([] as Agreement[]));

        return;
    }

    return { tokensByAgreements, filteredAgreements };
}

/**
 *  Load token balances and prices
 */
export function* fetchTokenBalances(
    filteredTokens: Token[],
    walletAddress: string,
    signer: Signer | Provider,
): Generator<any, Token[], Token[]> {
    const tokenBalances = yield all(
        filteredTokens.map((token) =>
            call(function* () {
                try {
                    const tokenContract = getSafeContract(token.address as string, IERCAbi, signer);
                    const userBalance = yield tokenContract.balanceOf(walletAddress);
                    token.userBalance = formatUnits(userBalance as string, token.decimals);
                    const response: Price = yield call(getPrice, token.address!, USD_ADDRESS);
                    token.price = response.price;
                } catch (e: any) {
                    Logger.error('Error in agreements::tokenContract:', { error: e });

                    yield put(
                        tokenPriceErrorAction({
                            message:
                                e.message || Errors.TOKEN_CONTRACT_NOT_AVAILABLE + ' ' + token.name,
                        }),
                    );
                }
                return token;
            }),
        ),
    );

    yield put(setAgreementTokensAction(tokenBalances as Token[]));
    return tokenBalances as Token[];
}

/**
 * Download agreement details
 */
export function* fetchAgreementsDetails(
    filteredAgreements: Agreement[],
    signer: Signer | Provider,
    role: ROLE = ROLE.LENDER,
) {
    const { walletAddress }: { walletAddress: string } = yield select((state: Store) => ({
        walletAddress: state.wallets.active.address,
    }));
    yield all(
        filteredAgreements.map((agreement) =>
            call(function* () {
                try {
                    const agreementContract = getSafeContract(
                        agreement.address,
                        AgreementABI,
                        signer,
                    );
                    const [metadata, state]: [Metadata, State] = yield agreementContract.info();

                    agreement.collaterals = Array.from(metadata.collaterals);
                    agreement.lenders = Array.from(metadata.liquidityProviders);
                    agreement.borrowers = Array.from(metadata.assetManagers);
                    agreement.tokens = Array.from(state.tokens);

                    const isLeverageAllowed = !excludedAddress.includes(metadata.leverage);

                    if (!isLeverageAllowed) {
                        yield put(
                            agreementLeverageErrorAction({
                                message: `${Errors.LEVERAGE_NOT_ALLOWED} ${metadata.leverage} and pool address ${agreement.address}`,
                            }),
                        );
                    }

                    const leverageContract = getSafeContract(metadata.leverage, IERCAbi, signer);

                    const [
                        totalPosition,
                        interestAccrued,
                        totalBorrowed,
                        totalDeposited,
                        agreementDetails,
                    ]: [bigint, bigint, bigint, bigint, AgreementDetails] = yield all([
                        agreementContract.balanceOf(walletAddress),
                        agreementContract.pendingRewards(walletAddress),
                        agreementContract.totalBorrowed(),
                        agreementContract.totalDeposited(),
                        call(getAgreementDetails, agreement.address),
                    ]);

                    agreement.totalPosition = formatUnits(
                        totalPosition,
                        agreement.leverage?.decimals,
                    );

                    agreement.interestAccrued = formatUnits(
                        interestAccrued,
                        agreement.leverage?.decimals,
                    );
                    agreement.totalBorrowed = formatUnits(
                        totalBorrowed,
                        agreement.leverage?.decimals,
                    );
                    agreement.totalDeposited = formatUnits(
                        totalDeposited,
                        agreement.leverage?.decimals,
                    );

                    if (totalDeposited === 0n) {
                        agreement.poolUtilization = '0';
                    } else {
                        agreement.poolUtilization = Math.min(
                            (Number(agreement.totalBorrowed) / Number(agreement.totalDeposited)) *
                                100,
                            100,
                        ).toFixed(2);
                    }

                    agreement.description = agreementDetails.description;
                    agreement.liquidationFee = agreementDetails.liquidationFee;
                    agreement.liquidationThreshold = agreementDetails.liquidationThreshold;
                    agreement.marginCallThreshold = agreementDetails.marginCallThreshold;
                    agreement.stressTestMatrix = agreementDetails.stressTestMatrix;

                    if (isLeverageAllowed) {
                        const poolBalance = yield leverageContract.balanceOf(agreement.address);

                        agreement.poolBalance = formatUnits(
                            poolBalance,
                            agreement.leverage.decimals,
                        );

                        agreement.withdrawable = Math.min(
                            Number(agreement.totalPosition!),
                            Number(agreement.poolBalance!),
                        ).toFixed(8);

                        agreement.interestClaimable = Math.min(
                            Number(agreement.interestAccrued!),
                            Number(agreement.poolBalance!),
                        ).toFixed(8);
                    }

                    agreement.totalDepositThreshold = formatUnits(
                        metadata.totalDepositThreshold,
                        agreement.leverage?.decimals,
                    );
                } catch (e: any) {
                    agreement.errorContract = true;
                    Logger.error('Error in agreements::agreementContract:', { error: e });

                    yield put(
                        agreementContractErrorAction({
                            message: agreement.address,
                        }),
                    );
                }
                return agreement;
            }),
        ),
    );

    if (role === ROLE.BORROWER) {
        yield put(setBorrowerAgreementsAction(filteredAgreements as Agreement[]));
    } else {
        yield put(setLenderAgreementsAction(filteredAgreements as Agreement[]));
    }
}

/**
 * Saga for fetching only contract-related data
 */
function* fetchAgreementsFromContractSaga(action: {
    type: string;
    payload: {
        filteredTokens: Token[];
        filteredAgreements: Agreement[];
        walletAddress: string;
        signer: Signer | Provider;
    };
}) {
    const { filteredTokens, filteredAgreements, walletAddress, signer } = action.payload;

    if (!signer) {
        console.error('No signer available!');
        return;
    }

    try {
        yield call(fetchTokenBalances, filteredTokens, walletAddress, signer);
        yield call(fetchAgreementsDetails, filteredAgreements, signer);
    } catch (e: any) {
        yield put(
            fetchAgreementsFromContractErrorAction({
                message: e.message || Errors.FETCH_AGREEMENTS_FROM_CONTRACT,
            }),
        );
    }
}

/**
 *  Main saga for loading agreements
 */
export function* getAgreementsSaga(action: {
    type: string;
    payload: {
        role: ROLE;
        withoutCache: boolean;
    };
}): Generator<any, void, any> {
    const { role: agreementRole, withoutCache } = action.payload;

    try {
        yield call(clearAgreementErrors);

        const walletData = yield call(getWalletInfoSaga, setAgreementsLoadingFalseAction);

        if (!walletData) {
            const isNoWallet = true;
            yield put(setAgreementsLoadingFalseAction());

            const data = yield call(fetchTokensAndAgreements, withoutCache);
            if (!data) return;
            const { tokens, agreements } = data;

            const filteredData = yield call(
                filterTokensAndAgreements,
                tokens,
                agreements,
                agreementRole,
                isNoWallet,
            );
            if (!filteredData) return;
            const { tokensByAgreements, filteredAgreements } = filteredData;
            const resultAgreements = mapAgreementData(filteredAgreements, tokensByAgreements);

            if (agreementRole === ROLE.BORROWER) {
                yield put(setBorrowerAgreementsAction(resultAgreements as Agreement[]));
            } else {
                yield put(setLenderAgreementsAction(resultAgreements as Agreement[]));
            }
            return;
        }

        const { walletAddress, signer } = walletData;

        const data = yield call(fetchTokensAndAgreements, withoutCache);
        if (!data) return;
        const { tokens, agreements } = data;

        if (!agreements || !agreements.length) {
            const action =
                agreementRole === ROLE.BORROWER
                    ? setBorrowerAgreementsAction
                    : setLenderAgreementsAction;

            yield put(action([] as unknown as Agreement[]));
            yield put(setTokensAction(tokens));
            return;
        }

        const filteredData = yield call(
            filterTokensAndAgreements,
            tokens,
            agreements,
            agreementRole,
        );

        if (agreementRole !== ROLE.BORROWER) {
            const borrowerData = yield call(
                filterTokensAndAgreements,
                tokens,
                agreements,
                ROLE.BORROWER,
            );

            if (borrowerData && borrowerData?.filteredAgreements?.length) {
                yield put(setUserBorrows(borrowerData.filteredAgreements));
            }
        }

        if (!filteredData) return;

        const { tokensByAgreements, filteredAgreements } = filteredData;

        if (agreementRole === ROLE.BORROWER) {
            yield put(setUserBorrows(filteredAgreements));
        }

        yield call(fetchTokenBalances, tokensByAgreements, walletAddress, signer);
        const resultAgreements = mapAgreementData(filteredAgreements, tokensByAgreements);
        yield call(fetchAgreementsDetails, resultAgreements, signer, agreementRole);
    } catch (e: any) {
        yield put(
            agreementGeneralErrorAction({
                message: Errors.NO_AVAILABLE_AGREEMENTS + ' ' + e.message,
            }),
        );
        Logger.error('Error in agreement::getAgreementsSaga:', { error: e });
    }
}

/**
 *  Clear wallet errors
 */
export function* clearWalletBalanceErrors() {
    yield all([
        put(clearErrorsAction({ errorType: AGREEMENTS_ERROR })),
        put(clearErrorsAction({ errorType: TOKENS_ERROR })),
        put(clearErrorsAction({ errorType: AGREEMENT_ROLE_ERROR })),
        put(clearErrorsAction({ errorType: TOKEN_BALANCE_ERROR })),
        put(clearErrorsAction({ errorType: WALLET_BALANCE_ERROR })),
    ]);
}

/**
 *  Check tokens and agreements
 */
export function* validateTokensAndAgreements(tokens: Token[], agreements: Agreement[]) {
    if (!agreements || !agreements.length) {
        return null;
    }

    if (!tokens) {
        yield put(tokenFetchErrorAction({ message: Errors.TOKENS }));
        return null;
    } else {
        yield put(setTokensAction(tokens));
    }

    return { tokens, agreements };
}

/**
 *  Filtering of tokens and agreements
 */
export function* filterTokensByAgreements(
    tokens: Token[],
    agreements: Agreement[],
    agreementRole: string,
) {
    const filteredTokens = tokens.filter(
        (token) => !excludedAddress.includes((token.address || '').trim()),
    );

    let filteredAgreements = agreements;

    if (agreementRole) {
        filteredAgreements = agreements.filter((agreement) => agreement.role === agreementRole);
    }

    if (agreementRole && !filteredAgreements.length) {
        yield put(
            agreementRoleErrorAction({ message: Errors.NO_AGREEMENTS_WITH_ROLE + agreementRole }),
        );
        return null;
    }

    return { filteredTokens, filteredAgreements };
}

/**
 *  Get token balance
 */
export function* fetchUserTokenBalances(
    filteredTokens: Token[],
    walletAddress: string,
    signer: Signer | Provider,
): Generator<any, Token[], any> {
    const tokenBalances = yield all(
        filteredTokens.map((token) =>
            call(function* () {
                try {
                    const tokenContract = getSafeContract(token.address as string, IERCAbi, signer);
                    const userBalance = yield tokenContract.balanceOf(walletAddress);
                    token.userBalance = formatUnits(userBalance as string, token.decimals);
                } catch (e: any) {
                    yield put(
                        tokenBalanceErrorAction({
                            message:
                                e.message || Errors.TOKEN_CONTRACT_NOT_AVAILABLE + ' ' + token.name,
                        }),
                    );
                    return { ...token };
                }
                return token;
            }),
        ),
    );

    yield put(setAgreementTokensAction(tokenBalances as Token[]));
    return tokenBalances;
}

/**
 *  Save to Store
 */
export function* saveAgreementsData(
    filteredAgreements: Agreement[],
    tokensByAgreements: Token[],
    role: ROLE,
) {
    const resultAgreements = mapAgreementData(filteredAgreements, tokensByAgreements);

    const action = role === ROLE.BORROWER ? setBorrowerAgreementsAction : setLenderAgreementsAction;

    yield put(action(resultAgreements as Agreement[]));
}

/**
 *  Main saga
 */
export function* getUserWalletBalanceSaga(action: {
    type: string;
    payload: {
        role: ROLE;
        withoutCache?: boolean;
    };
}): Generator<any, void, any> {
    const { role: agreementRole } = action.payload;

    try {
        yield call(clearWalletBalanceErrors);

        const walletData = yield call(getWalletInfoSaga, setAgreementsLoadingFalseAction);
        if (!walletData) return;
        const { walletAddress, signer } = walletData;

        const { tokens } = yield select((state: Store) => ({
            tokens: state.tokens.agreementTokens,
        }));

        const agreements =
            agreementRole === ROLE.BORROWER
                ? yield select((state: Store) => state.agreements.borrowerAgreements)
                : yield select((state: Store) => state.agreements.lenderAgreements);

        const validatedData = yield call(validateTokensAndAgreements, tokens, agreements);
        if (!validatedData) return;
        const { tokens: validTokens, agreements: validAgreements } = validatedData;

        const filteredData = yield call(
            filterTokensByAgreements,
            validTokens,
            validAgreements,
            agreementRole,
        );
        if (!filteredData) return;
        const { filteredTokens, filteredAgreements } = filteredData;

        const tokenBalances = yield call(
            fetchUserTokenBalances,
            filteredTokens,
            walletAddress,
            signer,
        );

        yield call(saveAgreementsData, filteredAgreements, tokenBalances, agreementRole);
    } catch (e: any) {
        yield put(walletBalanceErrorAction({ message: Errors.WALLET_BALANCE + ' ' + e.message }));
    }
}

export function* agreementsWatcher() {
    yield takeLatest(GET_AGREEMENTS, getAgreementsSaga);
    yield takeLatest(GET_USER_WALLET_BALANCE, getUserWalletBalanceSaga);
    yield takeLatest(FETCH_AGREEMENTS_FROM_CONTRACT, fetchAgreementsFromContractSaga);
}
